import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorBox, Loading } from "@/components/common/States";
import { AuthShell, Field, Submit, Consents } from "./shared";
import { useAfterLogin } from "./Login";

/** Two-step "request code -> confirm with code" flow, used for reset and unlock. */
function CodeFlow({ testId, title, subtitle, requestPath, confirmPath, withPassword }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState({ new_password: "", new_password_confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (!sent) {
        toast.success((await api.post(requestPath, { email })).data.message);
        setSent(true);
      } else {
        if (withPassword && pw.new_password !== pw.new_password_confirm) throw { response: { data: { detail: "Şifreler eşleşmiyor" } } };
        toast.success((await api.post(confirmPath, { email, code, ...(withPassword ? pw : {}) })).data.message);
        nav(`/giris`);
      }
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title={title} subtitle={subtitle} testId={testId}>
      <form onSubmit={submit} className="space-y-4">
        <Field id={`${testId}-email-input`} label="E-posta" type="email" value={email} disabled={sent} onChange={(e) => setEmail(e.target.value)} required />
        {sent && (
          <>
            <Field id={`${testId}-code-input`} label="E-postanıza gelen kod" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
            {withPassword && (
              <>
                <Field id={`${testId}-password-input`} label="Yeni şifre" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required />
                <Field id={`${testId}-password-confirm-input`} label="Yeni şifre tekrar" type="password" value={pw.new_password_confirm} onChange={(e) => setPw({ ...pw, new_password_confirm: e.target.value })} required />
              </>
            )}
          </>
        )}
        {err && <ErrorBox message={err} />}
        <Submit busy={busy} testId={`${testId}-submit-button`}>{sent ? "Onayla" : "Kod gönder"}</Submit>
      </form>
    </AuthShell>
  );
}

export const Forgot = () => (
  <CodeFlow testId="forgot" title="Şifremi unuttum" subtitle="E-postanıza gelen kodla yeni şifre belirleyin"
    requestPath="/auth/forgot-password" confirmPath="/auth/reset-password" withPassword />
);

export const Unlock = () => (
  <CodeFlow testId="unlock" title="Hesap kilidini aç" subtitle="5 kez hatalı şifre girildiğinde hesap kilitlenir. E-postanıza gelen kodla kilidi açabilirsiniz."
    requestPath="/auth/unlock/request" confirmPath="/auth/unlock/confirm" />
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function GoogleCallback() {
  const [params] = useSearchParams();
  const after = useAfterLogin();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const expected = sessionStorage.getItem("g_state");
    if (!params.get("code") || params.get("state") !== expected) { setErr("Google girişi doğrulanamadı, tekrar deneyin"); return; }
    api.post("/auth/google", { code: params.get("code"), redirect_uri: `${window.location.origin}/auth/google` })
      .then(({ data }) => {
        if (data.requires_2fa) { toast.info("2 aşamalı doğrulama kodu e-postanıza gönderildi"); nav("/giris"); return; }
        after(data);
      })
      .catch((e) => setErr(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <AuthShell title="Google ile giriş" testId="google-callback-page">{err ? <ErrorBox message={err} /> : <Loading label="Google hesabı doğrulanıyor…" />}</AuthShell>;
}

export function CompleteProfile() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: user?.full_name || "", phone: "" });
  const [consents, setConsents] = useState({ terms: false, kvkk: false, privacy: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/complete-profile", { ...f, consents });
      setUser(data.user);
      toast.success("Profiliniz tamamlandı");
      nav("/");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title="Profilinizi tamamlayın" subtitle="Google hesabınızla kayıt için birkaç bilgi gerekiyor" testId="complete-profile-page">
      <form onSubmit={submit} className="space-y-4">
        <Field id="complete-name-input" label="İsim soyisim" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required />
        <Field id="complete-phone-input" label="Telefon numarası" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required />
        <p className="text-xs text-zinc-500">İlanlarda farklı bir numara kullanabilirsiniz.</p>
        <Consents value={consents} onChange={setConsents} />
        {err && <ErrorBox message={err} />}
        <Submit busy={busy} testId="complete-submit-button">Kaydet ve devam et</Submit>
      </form>
    </AuthShell>
  );
}
